/**
 * Shared utility functions for prompt feedback features
 * Used by PromptFeedbackDrawer, ReportPromptForm, DisputeDrawer, etc.
 */

// Score mappings - short form (Pass/Fail)
export const SCORE_TO_LABEL = { 1: 'Pass', 0: 'Fail', [-1]: 'N/A' }

// Score mappings - past tense (Passed/Failed) for displaying historical scores
export const SCORE_TO_STATUS = { 1: 'Passed', 0: 'Failed', [-1]: 'N/A' }

// Score options for radio buttons and dropdowns
export const SCORE_OPTIONS = [
  { score: 1, label: 'Pass', value: 'pass', testId: 'pass', className: 'pass' },
  { score: 0, label: 'Fail', value: 'fail', testId: 'fail', className: 'fail' },
  { score: -1, label: 'N/A', value: 'n/a', testId: 'na', className: '' },
]

// Score value mappings (lowercase)
const SCORE_TO_VALUE = { 1: 'pass', 0: 'fail', [-1]: 'n/a' }
const LABEL_TO_SCORE = { pass: 1, fail: 0, 'n/a': -1 }

// Value normalization mappings
const PASS_VALUES = ['yes', 'pass', 'true', '1']
const FAIL_VALUES = ['no', 'fail', 'false', '0']
const NA_VALUES = ['n/a', 'na', 'null', '-1']

/**
 * Normalize a value to lowercase trimmed string for comparison
 */
const normalizeValue = (value) => {
  if (!value) {
    return null
  }
  return String(value).toLowerCase().trim()
}

/**
 * Parse AI response JSON to extract just the response value (yes/no/N/A)
 * @param {string|object} aiResponse - The AI response (may be JSON string or object)
 * @returns {string|null} - The parsed response value
 */
export const parseAiResponse = (aiResponse) => {
  if (!aiResponse) {
    return null
  }

  if (typeof aiResponse === 'string') {
    try {
      const parsed = JSON.parse(aiResponse)
      return parsed.response || aiResponse
    } catch {
      return aiResponse
    }
  }

  if (typeof aiResponse === 'object' && aiResponse.response) {
    return aiResponse.response
  }

  return aiResponse
}

/**
 * Normalize all result formats to Pass/Fail/N/A display labels
 * Handles: yes/no, pass/fail, true/false, 1/0/-1
 * @param {*} value - The value to normalize
 * @returns {string|null} - 'Pass', 'Fail', 'N/A', or the original value
 */
export const getDisplayLabel = (value) => {
  if (!value) {
    return null
  }

  const parsedValue = parseAiResponse(value)
  if (!parsedValue) {
    return null
  }

  const lower = normalizeValue(parsedValue)

  if (PASS_VALUES.includes(lower)) {
    return 'Pass'
  }
  if (FAIL_VALUES.includes(lower)) {
    return 'Fail'
  }
  if (NA_VALUES.includes(lower)) {
    return 'N/A'
  }

  return parsedValue
}

/**
 * Convert AI response (yes/no) to Pass/Fail based on must_be_present
 * @param {string} aiResponse - The AI response (yes/no/N/A)
 * @param {boolean} mustBePresent - If true, yes=Pass; if false, no=Pass
 * @returns {string|null} - 'Pass', 'Fail', 'N/A', or original value
 */
export const aiResponseToPassFail = (aiResponse, mustBePresent = true) => {
  if (!aiResponse) {
    return null
  }

  const parsed = parseAiResponse(aiResponse)
  const lower = normalizeValue(parsed)

  const yesResult = mustBePresent ? 'Pass' : 'Fail'
  const noResult = mustBePresent ? 'Fail' : 'Pass'

  if (lower === 'yes') {
    return yesResult
  }
  if (lower === 'no') {
    return noResult
  }
  if (['n/a', 'na'].includes(lower)) {
    return 'N/A'
  }

  return parsed
}

/**
 * Convert Pass/Fail to AI response (yes/no) based on must_be_present
 * Inverse of aiResponseToPassFail
 * @param {string} passFail - 'pass', 'fail', or 'n/a'
 * @param {boolean} mustBePresent - If true, Pass=yes; if false, Pass=no
 * @returns {string|null} - 'yes', 'no', 'N/A', or original value
 */
export const passFailToAiResponse = (passFail, mustBePresent = true) => {
  if (!passFail) {
    return null
  }

  const lower = normalizeValue(passFail)

  const passResult = mustBePresent ? 'yes' : 'no'
  const failResult = mustBePresent ? 'no' : 'yes'

  if (lower === 'pass') {
    return passResult
  }
  if (lower === 'fail') {
    return failResult
  }
  if (lower === 'n/a') {
    return 'N/A'
  }

  return passFail
}

/**
 * Convert AI response (yes/no/N/A) to numeric score based on must_be_present
 * @param {string|object} aiResponse - The AI response (may be JSON string or object)
 * @param {boolean} mustBePresent - If true, yes=1; if false, no=1
 * @param {number} fallbackScore - Score to return if ai_response can't be parsed
 * @returns {number} - 1 (pass), 0 (fail), or -1 (N/A)
 */
export const aiResponseToScore = (aiResponse, mustBePresent = true, fallbackScore = null) => {
  const passFail = aiResponseToPassFail(aiResponse, mustBePresent)
  if (!passFail) {
    return fallbackScore
  }
  const score = LABEL_TO_SCORE[passFail.toLowerCase()]
  return score !== undefined ? score : fallbackScore
}

/**
 * Check if a value represents a passing result
 * @param {*} value - The value to check
 * @returns {boolean}
 */
export const isPassResult = (value) => {
  if (!value) {
    return false
  }
  const parsed = parseAiResponse(value)
  const lower = normalizeValue(parsed)
  return PASS_VALUES.includes(lower)
}

/**
 * Check if a value represents a failing result
 * @param {*} value - The value to check
 * @returns {boolean}
 */
export const isFailResult = (value) => {
  if (!value) {
    return false
  }
  const parsed = parseAiResponse(value)
  const lower = normalizeValue(parsed)
  return FAIL_VALUES.includes(lower)
}

/**
 * Convert numeric score (1, 0, -1) to label (Pass/Fail/N/A)
 * @param {number} score - The numeric score
 * @returns {string}
 */
export const scoreToLabel = (score) => {
  return SCORE_TO_LABEL[score] ?? String(score)
}

/**
 * Convert numeric score (1, 0, -1) to status (Passed/Failed/N/A)
 * Use this for displaying historical scores in past tense
 * @param {number} score - The numeric score
 * @returns {string}
 */
export const scoreToStatus = (score) => {
  return SCORE_TO_STATUS[score] ?? 'Invalid Score'
}

/**
 * Convert numeric score (1, 0, -1) to dropdown value ('pass', 'fail', 'n/a')
 * @param {number} score - The numeric score
 * @returns {string}
 */
export const scoreToDropdownValue = (score) => {
  return SCORE_TO_VALUE[score] ?? ''
}

/**
 * Convert dropdown value ('pass', 'fail', 'n/a') to numeric score (1, 0, -1)
 * @param {string} value - The dropdown value
 * @returns {number|null}
 */
export const dropdownValueToScore = (value) => {
  if (!value) {
    return null
  }
  const lower = normalizeValue(value)
  return LABEL_TO_SCORE[lower] ?? null
}

/**
 * Check if evidence is a message type
 * @param {object} evidence - Evidence object
 * @returns {boolean}
 */
const isMessageType = (evidence) => {
  return evidence?.call_type === 'message'
}

/**
 * Get the call_id or message_id from evidence based on call_type
 * @param {object} evidence - Evidence object with call_id, message_id, call_type
 * @returns {string|null}
 */
export const getEvidenceCallId = (evidence) => {
  if (isMessageType(evidence)) {
    return evidence?.message_id
  }
  return evidence?.call_id
}

/**
 * Build the link path for an evidence item
 * @param {object} evidence - Evidence object
 * @returns {string}
 */
export const getEvidenceLink = (evidence) => {
  if (isMessageType(evidence)) {
    return `/messages/${evidence?.message_id}`
  }
  return `/calls/${evidence?.call_id}`
}

/**
 * Find a criteria by ID within a scorecard config's nested structure
 * @param {object} config - The scorecard config
 * @param {string} criteriaId - The criteria ID to find
 * @returns {object|null}
 */
export const findCriteriaInConfig = (config, criteriaId) => {
  if (!criteriaId || !config?.sections) {
    return null
  }

  const allCriteria = (config.sections || []).flatMap((section) =>
    (section.measures || []).flatMap((measure) => measure.criteria || [])
  )

  return allCriteria.find((criteria) => criteria.id === criteriaId) || null
}

/**
 * Find scorecard config and criteria by IDs
 * @param {string|null} configId - The scorecard config ID
 * @param {string|null} criteriaId - The scorecard criteria ID
 * @param {Array} scorecardConfigs - All loaded scorecard configs
 * @returns {{ config: object|null, criteria: object|null }}
 */
export const findScorecardConfigAndCriteria = (configId, criteriaId, scorecardConfigs) => {
  if (!configId || !scorecardConfigs?.length) {
    return { config: null, criteria: null }
  }

  const config = scorecardConfigs.find((scorecardConfig) => scorecardConfig.id === configId)
  if (!config) {
    return { config: null, criteria: null }
  }

  const criteria = findCriteriaInConfig(config, criteriaId)
  return { config, criteria }
}

/**
 * Get scorecard lookup data from evidence
 * @param {Array} allEvidence - All evidence items for the task
 * @param {Array} scorecardConfigs - All loaded scorecard configs
 * @returns {{
 *   matchingScorecardConfig: object|null,
 *   matchingCriteria: object|null,
 *   mustBePresent: boolean,
 *   scorecardConfigId: string|null,
 *   scorecardConfigSid: string|null,
 *   scorecardCriteriaId: string|null
 * }}
 */
export const getScorecardLookupData = (allEvidence, scorecardConfigs) => {
  const firstEvidence = allEvidence?.[0]
  const scorecardConfigId = firstEvidence?.scorecard_config_id
  const scorecardConfigSid = firstEvidence?.scorecard_config_sid
  const scorecardCriteriaId = firstEvidence?.scorecard_criteria_id

  const { config: matchingScorecardConfig, criteria: matchingCriteria } =
    findScorecardConfigAndCriteria(scorecardConfigId, scorecardCriteriaId, scorecardConfigs)

  const mustBePresent = matchingCriteria?.must_be_present ?? true

  return {
    matchingScorecardConfig,
    matchingCriteria,
    mustBePresent,
    scorecardConfigId,
    scorecardConfigSid,
    scorecardCriteriaId,
  }
}
